(ns insta.render
  "Helper functions for server-side rendering of `<insta-*>` stories.
   Exposed as `window.__instaRender` so the Clojure renderer can call
   them via Chrome DevTools Protocol `evaluate`."
  (:require [clojure.string :as str]))

(def ^:private sticker-tags
  (str "insta-hashtag,insta-mention,insta-location,insta-link,"
       "insta-poll,insta-question,insta-quiz,insta-countdown,insta-emoji-slider"))

(defn wait-for-components
  "Returns a Promise that resolves when every sticker's Shadow DOM has
   rendered content. Polls up to 50 times (5s), then waits two extra
   animation frames."
  []
  (js/Promise.
    (fn [resolve]
      (let [attempts (atom 0)]
        (letfn [(poll []
                  (let [els (js/document.querySelectorAll sticker-tags)
                        ready (loop [i 0]
                                (if (>= i (.-length els))
                                  true
                                  (let [sr (.-shadowRoot (.item els i))]
                                    (if (or (nil? sr)
                                            (zero? (.-childElementCount sr)))
                                      false
                                      (recur (inc i))))))]
                    (if (or ready (> @attempts 50))
                      (js/requestAnimationFrame
                        (fn []
                          (js/requestAnimationFrame
                            (fn [] (resolve "ready")))))
                      (do (swap! attempts inc)
                          (js/setTimeout poll 100)))))]
          (poll))))))

(defn- transform-matrix
  "The CSS transform of `el` combined with the transforms of its parents
   up to, without, `story`: a sticker inside a rotated or scaled wrapper
   is rotated and scaled on the page as well. Only the rotation and the
   scale of the matrix are used, the bounding rect already contains every
   translation. The browser ignores a transform on an inline element
   (an `<insta-*>` element is inline unless styled otherwise), so it is
   ignored here as well."
  [el story]
  (loop [node el
         m (js/DOMMatrix.)]
    (if (or (nil? node) (= node story))
      m
      (let [style (js/getComputedStyle node)
            t (.-transform style)]
        (recur (.-parentElement node)
               (if (or (not t)
                       (= t "none")
                       (= "inline" (.-display style)))
                 m
                 (.multiply (js/DOMMatrix. t) m)))))))

(defn- self-and-parents
  "`el` and its parents up to, without, `story`."
  [el story]
  (take-while #(and (some? %) (not= % story))
              (iterate #(.-parentElement %) el)))

(defn- box
  [r]
  {:left (.-left r) :top (.-top r) :right (.-right r) :bottom (.-bottom r)})

(defn- intersect
  [a b]
  (let [result {:left (max (:left a) (:left b))
                :top (max (:top a) (:top b))
                :right (min (:right a) (:right b))
                :bottom (min (:bottom a) (:bottom b))}]
    (when (and (< (:left result) (:right result))
               (< (:top result) (:bottom result)))
      result)))

(defn- contains-box?
  "With 1px tolerance for fractional layout."
  [outer inner]
  (and (<= (:left outer) (+ (:left inner) 1))
       (<= (:top outer) (+ (:top inner) 1))
       (>= (+ (:right outer) 1) (:right inner))
       (>= (+ (:bottom outer) 1) (:bottom inner))))

(defn- faded?
  "Partly transparent (opacity 0 is hidden, see `:visible`) or filtered,
   on itself or a parent."
  [el story]
  (let [styles (map js/getComputedStyle (self-and-parents el story))]
    (boolean
      (or (< (reduce * (map #(js/parseFloat (.-opacity %)) styles)) 0.99)
          (some #(not= "none" (.-filter %)) styles)))))

(defn- skewed?
  "The transform of the sticker and its parents is skewed: its axes are
   no longer at right angles, which a clip transform cannot show."
  [el story]
  (let [m (transform-matrix el story)
        a (.-a m) b (.-b m) c (.-c m) d (.-d m)
        norm (* (js/Math.hypot a b) (js/Math.hypot c d))]
    (and (pos? norm)
         (> (/ (js/Math.abs (+ (* a c) (* b d))) norm) 0.01))))

(defn- sample-points
  "Nine points inside the box, away from its edges."
  [{:keys [left top right bottom]}]
  (for [fx [0.2 0.5 0.8]
        fy [0.2 0.5 0.8]]
    [(+ left (* fx (- right left)))
     (+ top (* fy (- bottom top)))]))

(defn- hits
  "The sticker elements and the other elements of the page at the point,
   topmost first: the sticker element of `els` for anything inside it.
   Hit-testing knows rotation and clipping, so a sticker is only there
   where the page shows it."
  [els story x y]
  (keep (fn [node]
          (or (some #(when (.contains % node) %) els)
              (when-not (.contains node story) node)))
        (js/document.elementsFromPoint x y)))

(defn- transparent-color?
  [color]
  (or (= "transparent" color)
      (boolean (re-find #"^rgba\(.*,\s*0\)$" color))))

(defn- text-at?
  "One of the node's own text lines is at the point."
  [node x y]
  (some (fn [child]
          (when (= 3 (.-nodeType child))
            (let [range (js/document.createRange)]
              (.selectNodeContents range child)
              (some (fn [r]
                      (and (<= (.-left r) x (.-right r))
                           (<= (.-top r) y (.-bottom r))))
                    (array-seq (.getClientRects range))))))
        (array-seq (.-childNodes node))))

(defn- painted-border?
  [style]
  (some (fn [side]
          (and (pos? (js/parseFloat (aget style (str "border" side "Width"))))
               (not= "none" (aget style (str "border" side "Style")))))
        ["Top" "Right" "Bottom" "Left"]))

(defn- see-through?
  "An element that the point hits without painting anything there: fully
   transparent, or without background, border, image or text line of its
   own at the point, like a full-size text layer or the wrapper of another
   sticker. What its children paint is hit as those children."
  [node x y]
  (let [style (js/getComputedStyle node)]
    (or (= "0" (.-opacity style))
        (and (= "none" (.-backgroundImage style))
             (transparent-color? (.-backgroundColor style))
             (not (painted-border? style))
             (not (#{"IMG" "VIDEO" "CANVAS" "svg"} (.-tagName node)))
             (not (text-at? node x y))))))

(defn- covered-at?
  "The sticker is at the point, but other content of the page is on top
   of it (above it, other stickers may be in between)."
  [el els story [x y]]
  (let [sticker? (set els)
        found (remove #(and (not (sticker? %))
                            (see-through? % x y))
                      (hits els story x y))]
    (and (some #{el} found)
         (some (complement sticker?) (take-while #(not= el %) found)))))

(defn- above-at?
  "Both stickers are at the point and the page shows `el` on top of
   `other` (a third sticker may be on top of both)."
  [el other els story x y]
  (let [stickers (vec (filter (set els) (hits els story x y)))
        i (.indexOf stickers el)
        j (.indexOf stickers other)]
    (< -1 i j)))

(defn- inside-box?
  [{:keys [left top right bottom]} [x y]]
  (and (<= left x right)
       (<= top y bottom)))

(defn- clipped-by?
  "The overflow of `node` cuts off part of the sticker: at a point of the
   sticker's box outside `node`, the page shows the sticker only once
   the overflow is visible. Hit-testing with and without the overflow
   knows rotation and whether `node` is in the sticker's containing
   block chain at all."
  [el els story node visible-box]
  (let [node-box (box (.getBoundingClientRect node))
        points (remove #(inside-box? node-box %) (sample-points visible-box))
        shown? (fn [[x y]] (some #{el} (hits els story x y)))]
    (when (seq points)
      ;; The background screenshot is taken of this page afterwards, so
      ;; the inline style and the scroll position are put back exactly
      (let [shown-now (mapv shown? points)
            style (.-style node)
            css-text (.-cssText style)
            scroll-top (.-scrollTop node)
            scroll-left (.-scrollLeft node)]
        (.setProperty style "overflow" "visible" "important")
        (try
          (some true? (map (fn [now point] (and (not now) (boolean (shown? point))))
                           shown-now
                           points))
          (finally
            (set! (.-cssText style) css-text)
            (set! (.-scrollTop node) scroll-top)
            (set! (.-scrollLeft node) scroll-left)))))))

(defn- clipped?
  "A parent's overflow cuts off part of the sticker that lies inside the
   story (the story itself cuts off the rest, on the page and in the
   story)."
  [el els story visible-box]
  (some (fn [node]
          (let [style (js/getComputedStyle node)]
            (and (or (not= "visible" (.-overflowX style))
                     (not= "visible" (.-overflowY style)))
                 (not (contains-box? (box (.getBoundingClientRect node))
                                     visible-box))
                 (clipped-by? el els story node visible-box))))
        (rest (self-and-parents el story))))

(defn- invisible-sticker?
  "`design=\"invisible\"` on a link, hashtag or mention, in any case, like
   `storrito.insta-components.render/invisible-sticker?`."
  [d]
  (and (#{"insta-link" "insta-hashtag" "insta-mention"} (aget d "tag"))
       (= "invisible" (some-> (aget d "design") str/trim str/lower-case))))

(defn- add-unsupported-css!
  "Marks what the page does with a sticker that the story cannot show,
   for the warnings of `storrito.insta-components.render/unsupported-css-warnings`:
   a sticker on top of a later one (z-index; the story layers the
   stickers in document order), covered by other content (in the story
   every sticker is above the rest of the page), cut off by a parent's
   overflow, faded or filtered, or skewed. Stickers get
   `pointer-events:auto` while the page is hit-tested."
  [story els stickers]
  (let [story-box (box (.getBoundingClientRect story))
        els (vec (js/Array.from els))
        pointer-events (mapv #(.. % -style -pointerEvents) els)
        ;; The canvas is what the page shows of a sticker, and the box
        ;; the story uses (see `extract-sticker-data`); a hidden sticker
        ;; is left out of the story, so it has no box here
        visible-boxes (mapv (fn [el d]
                              (when-not (false? (aget d "visible"))
                                (let [canvas (some-> (.-shadowRoot el)
                                                     (.querySelector "canvas"))]
                                  (intersect (box (.getBoundingClientRect (or canvas el)))
                                             story-box))))
                            els
                            (array-seq stickers))]
    (doseq [el els]
      (set! (.. el -style -pointerEvents) "auto"))
    (try
      (dotimes [i (count els)]
        (let [el (els i)
              d (aget stickers i)
              visible-box (visible-boxes i)]
          (when visible-box
            (aset d "covered"
                  (boolean (some #(covered-at? el els story %)
                                 (sample-points visible-box))))
            (aset d "clipped" (boolean (clipped? el els story visible-box)))
            (aset d "faded" (faded? el story))
            (aset d "skewed" (skewed? el story))
            (aset d "aboveLater"
                  (boolean
                    (some (fn [j]
                            (when-let [overlap (some-> (visible-boxes j)
                                                       (intersect visible-box))]
                              (some (fn [[x y]] (above-at? el (els j) els story x y))
                                    (sample-points overlap))))
                          ;; an invisible sticker has nothing to cover
                          (remove #(invisible-sticker? (aget stickers %))
                                  (range (inc i) (count els)))))))))
      (finally
        (doseq [[el value] (map vector els pointer-events)]
          (set! (.. el -style -pointerEvents) value))))))

(defn extract-sticker-data
  "Queries all `<insta-*>` elements inside `<insta-story>`, reads their
   attributes, bounding rects, and the CSS transform rotation of the
   sticker and its parents. Returns a JSON
   string with `{stickers: [...], videoSrc: ...}`."
  []
  (let [story (js/document.querySelector "insta-story")]
    (if-not story
      (js/JSON.stringify #js {:stickers #js [] :videoSrc nil})
      (let [video-src (.getAttribute story "src")
            els (.querySelectorAll story sticker-tags)
            sr (.getBoundingClientRect story)
            stickers #js []]
        (dotimes [i (.-length els)]
          (let [el (.item els i)
                r (.getBoundingClientRect el)
                ;; The transforms of the sticker and its parents, as one
                ;; matrix. We extract rotation and uniform scale.
                ;; - translate: already reflected in getBoundingClientRect() → :x/:y
                ;; - skew: editor2 :clip/transform has no skew; not useful for stickers
                ;; - non-uniform scale: Instagram stickers don't support it
                m (transform-matrix el story)
                rotation (js/Math.atan2 (.-b m) (.-a m))
                css-scale (js/Math.hypot (.-a m) (.-b m))
                canvas (some-> (.-shadowRoot el)
                               (.querySelector "canvas"))
                ;; The sticker is what its canvas shows, which can be
                ;; smaller than the element: the canvas is never scaled
                ;; above its own size (`insta.components/set-shadow-canvas!`),
                ;; e.g. in an element with `width:100%`.
                cr (if canvas (.getBoundingClientRect canvas) r)
                d #js {:tag (.toLowerCase (.-tagName el))
                       :x (- (.-left cr) (.-left sr))
                       :y (- (.-top cr) (.-top sr))
                       :width (if canvas (.-width canvas) (.-width r))
                       :height (if canvas (.-height canvas) (.-height r))
                       :rectWidth (.-width cr)
                       :rectHeight (.-height cr)
                       ;; The canvas size from the layout, before any
                       ;; transform: the size of a rotated sticker, whose
                       ;; rect is the bounding box of the rotated canvas
                       :layoutWidth (if canvas (.-offsetWidth canvas) (.-offsetWidth el))
                       :layoutHeight (if canvas (.-offsetHeight canvas) (.-offsetHeight el))
                       :rotation rotation
                       :cssScale css-scale
                       ;; An absolutely placed element without a width of
                       ;; its own is laid out in the room right of its
                       ;; `left`, so its canvas may be squeezed
                       ;; (`storrito.insta-components.render/squeezed-sticker-warning`)
                       :positionedAbsolute (contains? #{"absolute" "fixed"}
                                                      (.-position (js/getComputedStyle el)))
                       ;; a width the author set: `width`, `max-width`, or
                       ;; `left` with `right`
                       :inlineWidth (let [style (.-style el)]
                                      (or (not= "" (.-width style))
                                          (not= "" (.-maxWidth style))
                                          (not= "" (.-right style))))
                       ;; false for a sticker the page does not show:
                       ;; display:none, visibility:hidden or opacity:0 on
                       ;; itself or a parent
                       :visible (.checkVisibility el #js {:opacityProperty true
                                                          :visibilityProperty true})}]
            (when-let [fc (.-_fabricCanvas el)]
              (when-let [obj (first (.getObjects fc))]
                (aset d "fabricWidth" (.-width obj))
                (aset d "fabricHeight" (.-height obj))))
            ;; Copy the element attributes, but never let an attribute
            ;; overwrite a measured key (:x, :y, :width, ...): attribute
            ;; values are strings and the server does arithmetic on the
            ;; measured numbers.
            (dotimes [j (.-length (.-attributes el))]
              (let [a (.item (.-attributes el) j)]
                (when (and (not= (.-name a) "class")
                           (not= (.-name a) "style")
                           (not (.hasOwnProperty d (.-name a))))
                  (aset d (.-name a) (.-value a)))))
            (.push stickers d)))
        ;; Only adds warnings, so it must never fail the extraction:
        (try
          (add-unsupported-css! story els stickers)
          (catch :default e
            (js/console.warn "Detecting unsupported sticker CSS failed" e)))
        (js/JSON.stringify #js {:stickers stickers
                                :videoSrc video-src})))))

(defn hide-video
  "Removes the `<video>` element from `<insta-story>`'s Shadow DOM and
   sets the story background to transparent, so only the page's text,
   shapes and images remain for the transparent PNG screenshot.  Only
   clears the background when a video was actually present — image-only
   stories keep their background intact. The backgrounds of the
   `<insta-story>` element and its parents (a wrapper, `body`, `html`)
   are cleared as well: on the page the story's black covers them,
   without it they would cover the video."
  []
  (when-let [story (js/document.querySelector "insta-story")]
    (when-let [shadow (.-shadowRoot story)]
      (when-let [video (.querySelector shadow "video")]
        (.remove video)
        (when-let [story-div (.querySelector shadow ".story")]
          (set! (.. story-div -style -background) "transparent"))
        (doseq [el (take-while some? (iterate #(.-parentElement %) story))]
          (.setProperty (.-style el) "background" "transparent" "important"))))))

(defn hide-stickers
  "Hides all sticker elements inside `<insta-story>` so only the
   background remains for the screenshot. Uses `visibility:hidden`,
   which keeps each sticker's layout box: `display:none` would take
   the stickers out of the flow, so that the text in the recommended
   flex column reflows into the space of the stickers, and the
   rendered story then shows the text under the stickers, at a spot
   where the extraction never measured it."
  []
  (when-let [story (js/document.querySelector "insta-story")]
    (let [els (.querySelectorAll story sticker-tags)]
      (dotimes [i (.-length els)]
        (set! (.. (.item els i) -style -visibility) "hidden")))))

(defn finish-animations
  "Jumps every CSS animation and transition of the page to its end, so
   that the extraction measures the page as it looks once it has settled:
   a sticker that fades in from opacity 0 would otherwise count as hidden
   and be left out. An animation that never ends cannot be finished and
   is cancelled instead, which shows the element without it."
  []
  (doseq [animation (.getAnimations js/document)]
    (try
      (.finish animation)
      (catch :default _
        (.cancel animation)))))

(defn init! []
  (set! js/window.__instaRender
        #js {:waitForComponents wait-for-components
             :finishAnimations finish-animations
             :extractStickerData extract-sticker-data
             :hideVideo hide-video
             :hideStickers hide-stickers}))
